
import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Chip, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import UploadOutlinedIcon from "@mui/icons-material/UploadOutlined";
import { toast } from "react-hot-toast";
import FilesPopup from "../../../../../components/EditProduct/FilesPopup";
import { getCustomerFilesWithMeta, uploadFile } from "../../../../../services/customerFiles";
import { isSupportedProductImageUrl } from "../../../../../utils/productImage";
import {
  quickProofProductUrlPath,
  quickProofPlacementUrlsPath,
  getQuickProofPlacementUrls,
  getQuickProofArtworkSlots,
  replaceQuickProofArtworkUrl,
  restoreQuickProofArtworkUrl,
  getQuickProofOriginalFileUrl,
  getQuickProofFileName,
  getQuickProofFileExtension,
  isQuickProofImageCandidate,
} from "./quickProofFileSlotUtils";

const isQuickProofRenderableImage = (url) =>
  isQuickProofImageCandidate(url) && isSupportedProductImageUrl(url);

const getDisplayArtworkName = (name, fileIndex, extension) => {
  const value = String(name || "").trim();
  // Keep original file URLs untouched; obscure storage UUIDs only in the visible label.
  if (!value || /^(?:[0-9a-f]{8}-){4}[0-9a-f]{12}(?:\.[a-z0-9]+)?$/i.test(value) ||
      /^[0-9a-f]{8}-[0-9a-f-]{16,}/i.test(value)) {
    return "Artwork file " + (fileIndex + 1) +
      (extension && extension !== "FILE" ? "." + extension.toLowerCase() : "");
  }
  return value;
};

const formatQuickProofFileSize = (size) => {
  const bytes = Number(size);
  if (!Number.isFinite(bytes) || bytes <= 0) return "";
  if (bytes < 1024) return Math.round(bytes) + " B";
  if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
};

const isDirectUrl = (raw = "") => {
  try {
    const uri = new URL(String(raw || "").trim());
    return (uri.protocol === "https:" || uri.protocol === "http:") &&
      !/\/generated-(estimate|sales-order)\//i.test(uri.pathname);
  } catch (_) {
    return false;
  }
};

/**
 * Each Binated artwork slot is independently selectable. Using the existing
 * Customer Files picker never edits the source Sales Order artwork/snapshot.
 */
export default function QuickProofFileSlots({
  sku = {},
  baseSku = {},
  skuIndex,
  customerId,
  orderId,
  instanceUniqueId,
  disabled = false,
  onOverride,
  onClearOverride,
}) {
  const [pickerTarget, setPickerTarget] = useState(null);
  const [customerFiles, setCustomerFiles] = useState([]);
  const [selectedNames, setSelectedNames] = useState({});
  const slots = useMemo(() => getQuickProofArtworkSlots(sku), [sku]);
  const automaticSlots = useMemo(() => getQuickProofArtworkSlots(baseSku), [baseSku]);

  useEffect(() => {
    if (!pickerTarget || !customerId) return undefined;
    let cancelled = false;
    getCustomerFilesWithMeta({ customerId }).then((files) => {
      if (!cancelled) setCustomerFiles(Array.isArray(files) ? files : []);
    }).catch((error) => {
      if (!cancelled) {
        setCustomerFiles([]);
        toast.error(error?.message || "Unable to load customer files.");
      }
    });
    return () => { cancelled = true; };
  }, [pickerTarget, customerId]);

  useEffect(() => {
    if (disabled) setPickerTarget(null);
  }, [disabled]);

  const openPicker = (target) => {
    if (disabled) return;
    if (!customerId) {
      toast.error("Attach a customer to this project before selecting artwork files.");
      return;
    }
    setPickerTarget(target);
  };

  const onUpload = async (selectedCustomerId, formData) => {
    if (!customerId || String(selectedCustomerId) !== String(customerId)) {
      throw new Error("Artwork upload requires the currently attached customer.");
    }
    if (typeof formData?.set === "function") {
      formData.set("folder", "binated-quick-proof");
      if (orderId) formData.set("orderId", String(orderId));
      if (instanceUniqueId) formData.set("instanceUniqueId", String(instanceUniqueId));
    }
    const uploaded = await uploadFile(customerId, formData);
    const currentFiles = await getCustomerFilesWithMeta({ customerId });
    setCustomerFiles(Array.isArray(currentFiles) ? currentFiles : []);
    return uploaded;
  };

  const onSelectFile = (selectedFile) => {
    if (!pickerTarget || disabled) return;
    const url = getQuickProofOriginalFileUrl(selectedFile);
    if (!isDirectUrl(url)) {
      toast.error("Choose a customer file with an original direct-download URL.");
      return;
    }
    if (pickerTarget.kind === "product" && !isQuickProofRenderableImage(url)) {
      toast.error("Product images must be image files. Use an artwork slot for .ai or .eps files.");
      return;
    }
    if (pickerTarget.kind === "product") {
      onOverride(pickerTarget.path, url);
    } else {
      const current = getQuickProofPlacementUrls(sku, pickerTarget.decorationIndex, pickerTarget.placementIndex);
      onOverride(pickerTarget.path, replaceQuickProofArtworkUrl(current, pickerTarget.urlIndex, url));
    }
    const labelKey = pickerTarget.path.join(".") + ":" + (pickerTarget.urlIndex || 0);
    setSelectedNames((previous) => ({
      ...previous,
      [labelKey]: selectedFile?.fileName || getQuickProofFileName(url),
    }));
    setPickerTarget(null);
  };

  const restoreArtwork = (slot) => {
    const path = quickProofPlacementUrlsPath(skuIndex, slot.decorationIndex, slot.placementIndex);
    const existing = getQuickProofPlacementUrls(sku, slot.decorationIndex, slot.placementIndex);
    const automatic = getQuickProofPlacementUrls(baseSku, slot.decorationIndex, slot.placementIndex);
    const restored = restoreQuickProofArtworkUrl(existing, automatic, slot.urlIndex);
    if (JSON.stringify(restored) === JSON.stringify(automatic)) onClearOverride(path);
    else onOverride(path, restored);
    const nameKey = path.join(".") + ":" + slot.urlIndex;
    setSelectedNames((previous) => {
      const next = { ...previous };
      delete next[nameKey];
      return next;
    });
  };

  const productUrl = String(sku?.product_url || "").trim();
  const originalProductUrl = String(baseSku?.product_url || "").trim();
  const productManual = productUrl !== originalProductUrl;
  const showProduct = isQuickProofRenderableImage(productUrl);

  return (
    <>
      <Box sx={{ minWidth: 0, width: "100%", display: "flex", flexDirection: "column", gap: 0.875 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 11.5, color: "#273653", lineHeight: "20px", height: 20, display: "block" }}>
          Product image
        </Typography>
        <Box sx={{
          width: "100%", height: 214, boxSizing: "border-box", p: 1,
          borderRadius: 1.5, border: productManual ? "1.5px solid #9f87f3" : "1px solid #dfe3f1",
          background: "linear-gradient(155deg, #fff 30%, #faf9ff 100%)",
          boxShadow: "0 4px 14px rgba(33, 40, 72, 0.06)",
          display: "flex", flexDirection: "column", gap: 0.75, minWidth: 0,
        }}>
          <Box sx={{ width: "100%", height: 155, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", borderRadius: 1.5 }}>
            {showProduct ? (
              <Box component="img" src={productUrl} alt="Product" sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
            ) : (
              <Typography variant="caption" sx={{ color: "text.secondary", textAlign: "center", px: 1 }}>No product image</Typography>
            )}
          </Box>
          <Button size="small" variant="outlined" startIcon={<UploadOutlinedIcon sx={{ fontSize: 17 }} />}
            disabled={disabled || !customerId}
            onClick={() => openPicker({ kind: "product", path: quickProofProductUrlPath(skuIndex), selectedUrl: productUrl })}
            sx={{
              mt: "auto", width: "100%", minWidth: 0, minHeight: 34, px: 0.5,
              fontSize: 11, fontWeight: 650, textTransform: "none", borderRadius: 1.75,
              whiteSpace: "nowrap", borderColor: "#cdd2ea", color: "#3c4b83",
              bgcolor: "#fff", "&:hover": { borderColor: "#9882e6", bgcolor: "#f7f5ff" },
            }}>
            Replace image
          </Button>
        </Box>
        {productManual && !disabled && (
          <Button size="small" sx={{ width: "100%", textTransform: "none", fontSize: 11 }}
            onClick={() => onClearOverride(quickProofProductUrlPath(skuIndex))}>Use automatic</Button>
        )}
      </Box>

      {slots.map((slot) => {
        const path = quickProofPlacementUrlsPath(skuIndex, slot.decorationIndex, slot.placementIndex);
        const original = automaticSlots.find((candidate) => candidate.key === slot.key)?.url || "";
        const manual = slot.url !== original;
        const nameKey = path.join(".") + ":" + slot.urlIndex;
        const name = selectedNames[nameKey] || getQuickProofFileName(slot.url);
        const extension = getQuickProofFileExtension(slot.url);
        const displayName = getDisplayArtworkName(name, slot.urlIndex, extension);
        const fileMeta = customerFiles.find((file) => getQuickProofOriginalFileUrl(file) === slot.url);
        const fileSize = formatQuickProofFileSize(fileMeta?.fileSize ?? fileMeta?.size);
        const showArtworkImage = isQuickProofRenderableImage(slot.url);
        const replaceArtwork = () => openPicker({
          kind: "artwork",
          path,
          decorationIndex: slot.decorationIndex,
          placementIndex: slot.placementIndex,
          urlIndex: slot.urlIndex,
          selectedUrl: slot.url,
        });
        return (
          <Box key={slot.key} sx={{ minWidth: 0, width: "100%", display: "flex", flexDirection: "column", gap: 0.875 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 11.5, color: "#273653", lineHeight: "20px", height: 20, display: "block" }} title={slot.placementName} noWrap>
              {slot.placementName} · File {slot.urlIndex + 1}
            </Typography>
            <Box sx={{
              width: "100%", height: 214, boxSizing: "border-box", p: 1,
              borderRadius: 1.5, border: manual ? "1.5px solid #8156e8" : slot.url ? "1.5px solid #ae95fb" : "1px solid #dfe3f1",
              background: "linear-gradient(155deg, #fff 25%, #f8f5ff 100%)",
              boxShadow: "0 4px 14px rgba(87, 65, 174, 0.11)",
              position: "relative", display: "flex", flexDirection: "column",
              alignItems: "center", gap: 0.375, minWidth: 0,
            }}>
              {Boolean(slot.url) && (
                <CheckCircleIcon aria-label="Artwork attached"
                  sx={{ position: "absolute", right: 7, top: 7, fontSize: 20, color: "#10a05a", bgcolor: "#fff", borderRadius: "50%", zIndex: 1 }} />
              )}
              <Box sx={{ height: 114, width: "100%", flexShrink: 0, display: "flex", justifyContent: "center", alignItems: "center", overflow: "hidden", borderRadius: 1.5 }}>
                {showArtworkImage ? (
                  <Box component="img" src={slot.url} alt={slot.placementName} sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: 1 }} />
                ) : (
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", color: "#6e55d6" }}>
                    <InsertDriveFileOutlinedIcon sx={{ fontSize: 38 }} />
                    <Typography variant="caption" sx={{ fontWeight: 800, fontSize: 10, lineHeight: 1.2 }}>
                      {slot.url ? extension : "No artwork"}
                    </Typography>
                  </Box>
                )}
              </Box>
              <Typography variant="caption" title={name} noWrap sx={{
                width: "100%", textAlign: "center", color: "#263252", fontWeight: 650,
                fontSize: 11, lineHeight: "15px", minHeight: 15,
              }}>{displayName}</Typography>
              <Typography variant="caption" sx={{ color: "#69779a", fontSize: 10.5, lineHeight: "14px", minHeight: 14 }}>
                {slot.url ? extension + (fileSize ? " • " + fileSize : "") : "No file selected"}
              </Typography>
              <Button size="small" variant="outlined" startIcon={<AutorenewIcon sx={{ fontSize: 17 }} />}
                disabled={disabled || !customerId} onClick={replaceArtwork}
                sx={{
                  mt: "auto", width: "100%", minWidth: 0, minHeight: 34, px: 0.5,
                  fontSize: 11, fontWeight: 650, textTransform: "none", borderRadius: 1.75,
                  borderColor: "#a995fa", color: "#4e2dc0", bgcolor: "#fff",
                  whiteSpace: "nowrap", "&:hover": { borderColor: "#7b56e8", bgcolor: "#f7f3ff" },
                }}>
                {slot.url ? "Replace file" : "Upload file"}
              </Button>
            </Box>
            {manual && <Chip size="small" variant="outlined" label="Manual file" sx={{ alignSelf: "flex-start", height: 21, fontSize: 10 }} />}
            {manual && !disabled && <Button size="small" sx={{ textTransform: "none", fontSize: 11 }} onClick={() => restoreArtwork(slot)}>Use automatic</Button>}
          </Box>
        );
      })}

      <FilesPopup
        open={Boolean(pickerTarget && !disabled)}
        onClose={() => setPickerTarget(null)}
        onImageSelect={() => {}}
        onSelectFile={onSelectFile}
        handleDone={onUpload}
        imageGroups={{ "Customer Files": customerFiles }}
        selectedImage={pickerTarget?.selectedUrl || ""}
        customerId={customerId || null}
        allowUpload={Boolean(customerId)}
      />
    </>
  );
}